// Who may use the back office, and where they land after sign-in.

/** @typedef {import('@popcart/core/v1').Me} Me */
/**
 * @typedef {{ kind: 'ops'; path: string }
 *   | { kind: 'shop'; path: string; shopId: string }
 *   | { kind: 'pick'; path: string; shopIds: string[] }
 *   | { kind: 'denied'; path: string }} Landing
 */

const SHOP_ROLES = ['shop_owner', 'shop_staff'];

/** @param {Me | null | undefined} me */
export function isPlatformAdmin(me) {
  return !!me && me.roles.some((r) => r.role === 'platform_admin');
}

/**
 * Shop ids this user works at, in the order the server listed them, without duplicates.
 * @param {Me | null | undefined} me
 * @returns {string[]}
 */
export function shopIdsFor(me) {
  if (!me) return [];
  /** @type {string[]} */
  const out = [];
  for (const r of me.roles) {
    if (SHOP_ROLES.includes(r.role) && r.shopId && !out.includes(r.shopId)) out.push(r.shopId);
  }
  return out;
}

/**
 * The short name for a shop this user works at (Me.roles[].shopSlug), or null when the server
 * didn't send one (old server, or a shop the user isn't on).
 * @param {Me | null | undefined} me
 * @param {string} shopId
 * @returns {string | null}
 */
export function shopSlugFor(me, shopId) {
  return me?.roles.find((r) => r.shopId === shopId && r.shopSlug)?.shopSlug ?? null;
}

/**
 * The Partner Hub address for a shop: /shop/<short name>, or /shop/<id> when no short name is
 * known (the hub then resolves it and replaces the address). docs/SHOP-SHORT-URLS.md
 * @param {string | null | undefined} slug
 * @param {string} [shopId]
 */
export function shopPath(slug, shopId) {
  return `/shop/${encodeURIComponent(slug || shopId || '')}`;
}

/** The Ops record address for a shop: /ops/shops/<short name> (or the id, resolved the same way). */
export function opsShopPath(/** @type {string | null | undefined} */ slug, /** @type {string} */ shopId = '') {
  return `/ops/shops/${encodeURIComponent(slug || shopId)}`;
}

/**
 * Where to send a freshly signed-in user. platform_admin wins; shop people go to their shop
 * (or a picker when they have several); everyone else has no back-office access.
 * @param {Me | null | undefined} me
 * @returns {Landing}
 */
export function landingFor(me) {
  if (isPlatformAdmin(me)) return { kind: 'ops', path: '/ops' };
  const ids = shopIdsFor(me);
  if (ids.length === 1) {
    const shopId = /** @type {string} */ (ids[0]);
    return { kind: 'shop', path: shopPath(shopSlugFor(me, shopId), shopId), shopId };
  }
  if (ids.length > 1) return { kind: 'pick', path: '/shops', shopIds: ids };
  return { kind: 'denied', path: '/no-access' };
}

/**
 * May this user open the Partner Hub for shopId? Admins may act on any shop.
 * @param {Me | null | undefined} me
 * @param {string} shopId
 */
export function canOpenShop(me, shopId) {
  return isPlatformAdmin(me) || shopIdsFor(me).includes(shopId);
}

/**
 * Role label for the nav footer.
 * @param {Me | null | undefined} me
 * @param {string | null} [shopId]
 */
export function roleLabel(me, shopId = null) {
  if (!me) return '';
  if (shopId) {
    const r = me.roles.find((x) => x.shopId === shopId && SHOP_ROLES.includes(x.role));
    if (r) return r.role === 'shop_owner' ? 'OWNER' : 'STAFF';
  }
  if (isPlatformAdmin(me)) return 'SUPER-ADMIN';
  return '';
}
